import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { withFormik } from 'formik';
import * as Yup from 'yup';
// components
import Modal from 'shared/modals/Modal';
import { FormikInput, FormikSelect } from 'shared/forms';
import toastr from 'shared/helpers/toastr';
import errorToastr from 'shared/helpers/errorToastr';
import LoadingModal from 'shared/loading/LoadingModal';
// store
import { getTruckMakes } from 'src/slices/truck/truckMakes';
// fetch
import { getTruckModel, postTruckModel, deleteTruckModel } from 'src/fetch/truckMakes';

const TruckModelModal = ({
  values,
  onClose,
  handleChange,
  handleBlur,
  handleSubmit,
  setFieldValue,
  onChange,
  touched,
  errors,
  isSubmitting,
  setSubmitting,
  resetForm,
}) => {
  const truckMakes = useSelector(getTruckMakes);

  const [isLoading, setIsLoading] = useState(false);

  const deleteModel = async () => {
    setSubmitting(true);

    try {
      await deleteTruckModel(values.id);
      onClose(true);
      resetForm();

      toastr('success', 'Truck Model Deleted Successfully.');
    } catch (error) {
      errorToastr({
        error,
        messagePrefix: 'Truck Model Delete Failed',
      });
      setSubmitting(false);
    }
  };

  useEffect(() => {
    if (values.id) {
      setIsLoading(true);
      getTruckModel(values.id)
        .then(resetForm)
        .finally(() => setIsLoading(false));
    }
  }, []);

  return (
    <Modal
      isOpen
      title={`${values && values.id ? 'Edit' : 'Add'} Truck Model`}
      handleClose={() => onClose()}
      containerStyle='medium'
    >
      <div className='modal-body-content'>
        <section className={`additional-charges-form ${isSubmitting ? 'hide-overflow' : ''}`}>
          <LoadingModal isOpen={isLoading || isSubmitting} isNested />
          <form onSubmit={handleSubmit} noValidate>
            <section className='modal-body formik-form'>
              <section className='formik-column'>
                <div className='formik-row'>
                  <FormikInput
                    type='text'
                    wrapperClass='wrapper-two-column'
                    label='Truck Model'
                    name='name'
                    required
                    value={values.name}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    touched={touched}
                    errors={errors}
                  />
                  <FormikSelect
                    wrapperClass='wrapper-two-column'
                    label='Truck Make'
                    options={truckMakes?.map(({ id, make }) => ({ label: make, value: id }))}
                    name='makeId'
                    required
                    overflowModal
                    onChange={({ value }) => setFieldValue('makeId', value)}
                    value={values.makeId}
                    onBlur={handleBlur}
                    touched={touched}
                    errors={errors}
                  />
                </div>
              </section>
            </section>
            <section className='modal-footer toolbar toolbar--xxs'>
              <div className='toolbar--column toolbar--right'>
                <button
                  type='submit'
                  className='button large secondary text-align--center toolbar--button--margin'
                  disabled={isSubmitting}
                >
                  Save
                </button>
                {values && values.id && (
                  <button
                    type='button'
                    className='button large error text-align--center toolbar--button--margin'
                    disabled={isSubmitting}
                    onClick={deleteModel}
                  >
                    Delete
                  </button>
                )}
                <button
                  type='button'
                  className='button large white text-align--center margin--none--xxs'
                  onClick={() => onClose()}
                >
                  Cancel
                </button>
              </div>
            </section>
          </form>
        </section>
      </div>
    </Modal>
  );
};

const TruckModelSchema = Yup.object().shape({
  name: Yup.string().nullable().required('Truck Model is Required'),
  makeId: Yup.number().nullable().required('Truck Make is Required'),
});

export default withFormik({
  enableReinitialize: true,
  mapPropsToValues: ({ initialValues = {} }) => ({ ...initialValues }),
  validationSchema: TruckModelSchema,
  handleSubmit: async (values, { setSubmitting, resetForm, props: { close } }) => {
    setSubmitting(true);

    postTruckModel({ ...values })
      .then(() => {
        close(true);
        resetForm();
        toastr('success', `Truck Model ${values.deleted ? 'Deleted' : 'Saved'} Successfully.`);
      })
      .catch((error) => {
        console.log('Error posting Truck Model', error);
        errorToastr({
          error,
          messagePrefix: 'Truck Model Update Failed',
        });
        setSubmitting(false);
      });
  },
})(TruckModelModal);
